import { createFileRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";
import z from "zod";
import { MonthwiseActivityTable } from "@/components/activity/months-activity";
import { ErrorDisplay } from "@/components/core/error-display";
import { CardLoadingSkeleton } from "@/components/core/loading-skeleton";
import { TabsComponent } from "@/components/core/tabs-component";
import { CustomerContacts } from "@/components/customer/contacts";
import { CustomerPolicies } from "@/components/customer/policies/policies";
import { Statements } from "@/components/customer/statements/statements";
import { CustomerSubcustomers } from "@/components/customer/subcustomers";
import { InvoicesTable } from "@/components/tables/invoices";
import { useGetCustomer } from "@/models/backend";

const tabs = [
  "activity",
  "invoices",
  "statements",
  "policies",
  "contacts",
  "subcustomers",
] as const;

const schema = z.object({
  tab: z.enum(tabs).default("activity"),
  showActivity: z.boolean().optional(),
});

export const Route = createFileRoute("/_authenticated/_contained/customers/$customerId/")({
  component: RouteComponent,
  validateSearch: schema,
});

function RouteComponent() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { customerId } = Route.useParams();
  const {
    data: customer,
    isLoading: isLoadingCustomer,
    isError: isErrorCustomer,
    error: customerError,
    refetch: refetchCustomer,
  } = useGetCustomer(customerId);

  if (isErrorCustomer) {
    return (
      <ErrorDisplay
        title="Failed to load customer"
        error={customerError}
        onRetry={() => refetchCustomer()}
      />
    );
  }

  if (isLoadingCustomer) {
    return <CardLoadingSkeleton />;
  }

  if (!customer) {
    return null;
  }

  const tabObjs: {
    label: string;
    component: ReactNode;
    id: (typeof tabs)[number];
  }[] = [
    {
      label: "Activity",
      component: (
        <MonthwiseActivityTable
          customerId={customer.id}
          pinnedNoteEventId={customer.pinned_note?.event_id ?? undefined}
        />
      ),
      id: "activity",
    },
    {
      label: "Invoices",
      component: (
        <InvoicesTable
          customerId={customer.id}
          enableSubcustomers={customer.parents.length === 0}
          embedded={false}
        />
      ),
      id: "invoices",
    },
    {
      label: "Statements",
      component: <Statements customer={customer} />,
      id: "statements",
    },
    {
      id: "policies",
      label: "Policies",
      component: <CustomerPolicies customer={customer} />,
    },
    {
      label: "Contacts",
      component: <CustomerContacts customer={customer} />,
      id: "contacts",
    },
  ];
  if (customer.children.length) {
    tabObjs.push({
      label: "Sub-customers",
      component: <CustomerSubcustomers customer={customer} />,
      id: "subcustomers",
    });
  }
  return (
    <TabsComponent
      tabs={tabObjs}
      selectedTab={tabObjs.findIndex((tab) => tab.id === search.tab)}
      onSelectedTab={(idx) => {
        navigate({ search: { tab: tabObjs[idx].id } });
      }}
    />
  );
}
